/**
 * structure_access — who may walk into a player's structure, PURE (2026-09-23).
 *
 * The console inside a structure sets an ENTRY POLICY (`structure_meta.entry`: 'public' — anyone
 * not banned; 'private' — the owner, admins and the entry list) and an ACCESS LIST
 * (`structure_permission` rows: owner / admin / entry / banned). The door (BuildingInteriorModule)
 * asks `canEnterStructure` before it opens; a refusal is a toast with the reason. Interiors are a
 * client construct, so this is the door's own rule (the server keeps the rows). The console's
 * seat inside the shell is decided here too, so a harness can pin it without a scene.
 */

export type EntryPolicy = 'public' | 'private';
export type PermLevel = 'owner' | 'admin' | 'entry' | 'banned';

export interface AccessQuery {
  /** The viewer is the structure's owner. */
  isOwner: boolean;
  /** The viewer's `structure_permission` level, if any. */
  level: PermLevel | null;
  /** The structure's entry policy ('public' when it has no meta row). */
  entry: EntryPolicy | string;
}

export function canEnterStructure(q: AccessQuery): { ok: boolean; why: string } {
  if (q.isOwner) return { ok: true, why: '' };
  if (q.level === 'banned') return { ok: false, why: 'You are banned from this structure' };
  if (q.entry === 'private') {
    if (q.level === 'owner' || q.level === 'admin' || q.level === 'entry') return { ok: true, why: '' };
    return { ok: false, why: 'This structure is private' };
  }
  return { ok: true, why: '' };
}

/** The display name: the console's name when set, else the type's label. */
export function structureDisplayName(metaName: string | null | undefined, typeLabel: string): string {
  const n = (metaName ?? '').trim();
  return n.length > 0 ? n : typeLabel;
}

export const CONSOLE_HEIGHT_M = 1.65;
/** How near the console a player must stand for its prompt. */
export const CONSOLE_USE_M = 2.4;

/** Where the console stands inside the shell, building-local: against the back wall (+Z, opposite the
 *  −Z door), centred, stepped sideways off the stair region if the stair reaches the back wall. */
export function consoleSeat(spec: { hw: number; hd: number; stair: { x: number; z: number; w: number; d: number } }): { x: number; z: number; yaw: number } {
  const z = spec.hd - 0.75;
  let x = 0;
  const s = spec.stair;
  const inStair = (px: number): boolean => Math.abs(px - s.x) <= s.w / 2 + 0.6 && Math.abs(z - s.z) <= s.d / 2 + 0.6;
  if (inStair(x)) {
    // Step away from the stair on the side with more room.
    const right = Math.min(spec.hw - 0.7, s.x + s.w / 2 + 1.0);
    const left = Math.max(-spec.hw + 0.7, s.x - s.w / 2 - 1.0);
    x = (spec.hw - right) >= (left + spec.hw) ? right : left;
  }
  // The generated console model's screen faces the model's +Z; it must face the room (−Z): yaw π.
  return { x, z, yaw: Math.PI };
}
